import {Store} from "vuex";
import {TradeContract} from "@/store/modules/hcs/tradeContract/model";
import {CONTRACT_MESSAGE_REQUEST_TYPES, TRADE_CONTRACT_STATUSES} from "@/constants";
import {DateTime} from "luxon";
import {toDateTime} from "@/utils/Formatter";
import {RootStateType} from "@/store";
import TradeContractBaseView, {
    ErrorArray,
    UninitializedStoreError
} from "@/components/pages/contract/TradeContract/TradeContractBaseView";
import {ContractProfile} from "@/store/modules/hcs/contractProfile/model";

type FormatError =
  'dataProfile.notReadyDataPropertyProfileMaps' |
  'serviceProfile.noServiceProfile' |
  'serviceProfile.noProcessProfile' |
  'serviceProfile.noEventProfile' |
  'contractProfile.noProfiles' |
  'complete.noMessage' |
  'complete.invalidExpireDate';

export type TradeContractSource = TradeContract & {
  message: string;
  dataPropertyProfileMapsIsReady: boolean;
  latestMessageCreatedAt: string;
};

/**
 * TradeContract 生成の為のデータソース
 */
export default class TradeContractView extends TradeContractBaseView<TradeContractSource> {
  /**
   * コンストラクタ
   *
   * @param tradeContract TradeContractSource
   * @param $store Store<RootStateType>
   */
  constructor(tradeContract: TradeContractSource, $store: Store<RootStateType>) {
    super(tradeContract, $store);
  }

  /**
   * すべての ContractProfile をロードします
   */
  async loadContractProfiles(): Promise<void> {
    const {data} = await this.hcsClient.get<ContractProfile[]>(`/contract_profiles`, {
      params: {
        tradeContractIdEq: this.id
      }
    });
    this.contractProfiles = data;
  }

  /**
   * エラーを取得します
   */
  errors(mode: 'adapt' | 'draft') {
    const result = new ErrorArray<FormatError>();
    if(mode === 'adapt' && !this.dataPropertyProfileMapsIsReady) {
      result.push('dataProfile.notReadyDataPropertyProfileMaps');
    }
    if(!this.selfServiceProfileId) {
      result.push('serviceProfile.noServiceProfile');
    }
    if(!this.selfProcessProfileId) {
      result.push('serviceProfile.noProcessProfile');
    }
    if(!(this.isSelfProduce || this.selfEventProfileIds.length > 0)) {
      result.push('serviceProfile.noEventProfile');
    }
    if(this.contractProfiles.length === 0) {
      result.push('contractProfile.noProfiles');
    }
    if(!this.message) {
      result.push('complete.noMessage');
    }
    if(!this.expireDateIsValid) {
      result.push('complete.invalidExpireDate');
    }
    return result;
  }

  /** 一つ前の TradeContract ID を取得します */
  get previousTradeContractId(): string | null {
    return this.tradeContractSource.previousTradeContractId;
  }

  /** 提携データをまとめて取得します */
  get companions() {
    return {
      ownerId: this.companionOwnerId,
      ownerName: this.companionOwnerName,
      terminalId: this.companionTerminalId,
      terminalName: this.companionTerminalName,
    };
  }

  /** 取引先事業者ID(表示用) */
  get companionOwnerId() {
    return this.tradeContractSource.companionOwnerId;
  }

  /** 取引先事業者名(表示用) */
  get companionOwnerName() {
    return this.tradeContractSource.companionOwnerName;
  }

  /** 取引先事業者サイトID(表示用) */
  get companionTerminalId() {
    return this.tradeContractSource.companionTerminalId;
  }

  /** 取引先事業者サイト名(表示用) */
  get companionTerminalName() {
    return this.tradeContractSource.companionTerminalName;
  }

  /** 契約作成プロセスにおいて、次の対応が要求されている側のターミナルID */
  get targetTerminalId() {
    return this.tradeContractSource.targetTerminalId;
  }

  /** 自分がターゲット側かどうか */
  get amiTarget() {
    return this.targetTerminalId === this.selfTerminalId;
  }

  /**
   * 募集型モードかどうかを取得します
   */
  get isPublishMode() {
    return this.contractType === 'recruitment' && this.baseId === null;
  }

  /**
   * 申込型モードかどうかを取得します
   */
  get isSubscribeMode() {
    return this.contractType === 'recruitment' && this.baseId !== null;
  }

  /** 契約作成プロセスにおいて相手に返信する期限を定める場合に設定する。nullの場合は期限がないことを示す。 */
  get expireDate() {
    const expireDate = this.tradeContractSource.expireDate;
    if(expireDate) {
      const dt = DateTime.fromISO(expireDate);
      if (dt.isValid) {
        return dt;
      }
    }
    return null;
  }
  set expireDate(expireDate: DateTime | Date | string | null) {
    expireDate = toDateTime(expireDate);
    if(!expireDate) {
      this.updateTradeContractSource({
        expireDate: null
      });
    } else {
      if (expireDate.isValid) {
        this.updateTradeContractSource({
          expireDate: expireDate.toISO()
        });
      } else {
        throw new Error(`${expireDate} is invalid format.`)
      }
    }
  }

  /** 回答期限が正しい場合は true そうでない場合 false */
  get expireDateIsValid() {
    const expireDate = this.expireDate;
    if(expireDate === null) {
      return false;
    }
    return expireDate >= DateTime.now().plus({days: 1}).startOf('day');
  }

  /** 契約開始日時 */
  get startDatetime() {
    const startDatetime = this.tradeContractSource.startDatetime;
    if(startDatetime) {
      const dt = DateTime.fromISO(startDatetime);
      if (dt.isValid) {
        return dt;
      }
    }
    return null;
  }
  set startDatetime(startDatetime: DateTime | Date | string | null) {
    startDatetime = toDateTime(startDatetime);
    if(!startDatetime) {
      this.updateTradeContractSource({
        startDatetime: null
      });
    } else {
      if (startDatetime.isValid) {
        this.updateTradeContractSource({
          startDatetime: startDatetime.toISO()
        });
      } else {
        throw new Error(`${startDatetime} is invalid format.`)
      }
    }
  }

  /** 契約終了日時 */
  get endDatetime() {
    const endDateime = this.tradeContractSource.endDatetime;
    if(endDateime) {
      const dt = DateTime.fromISO(endDateime);
      if (dt.isValid) {
        return dt;
      }
    }
    return null;
  }
  set endDatetime(endDatetime: DateTime | Date | string | null) {
    endDatetime = toDateTime(endDatetime);
    if(!endDatetime) {
      this.updateTradeContractSource({
        endDatetime: null
      });
    } else {
      if (endDatetime.isValid) {
        this.updateTradeContractSource({
          endDatetime: endDatetime.toISO()
        });
      } else {
        throw new Error(`${endDatetime} is invalid format.`)
      }
    }
  }

  /** メッセージ */
  get message() {
    if(this.isPublishMode) {
      return ' ';
    }
    return this.tradeContractSource.message || '';
  }
  set message(message) {
    this.updateTradeContractSource({message});
  }

  /** 最終データ送受信日時 (/trade_contracts/results API) */
  get latestMessageCreatedAt() {
    const latestMessageCreatedAt = toDateTime(this.tradeContractSource.latestMessageCreatedAt);
    if(latestMessageCreatedAt?.isValid) {
      return latestMessageCreatedAt;
    }
    return null;
  }

  /** ステータス */
  get status() {
    return this.tradeContractSource.status;
  }
  set status(status) {
    this.tradeContractSource.status = status;
  }

  /** ステータス文字列 */
  get statusText() {
    if(this.status === 'preparing') {
      if(this.isPublishMode) {
        return '募集準備中';
      } else if(this.isSubscribeMode) {
        return '申込準備中';
      } else if(this.isRequestMode) {
        return '申請準備中';
      } else {
        return '準備中';
      }
    } else {
      return TRADE_CONTRACT_STATUSES[this.status];
    }
  }

  /** baseId */
  get baseId() {
    return this.tradeContractSource.baseId;
  }

  /** データ項目プロパティーマップが準備完了かどうか */
  get dataPropertyProfileMapsIsReady(): boolean {
    return this.tradeContractSource.dataPropertyProfileMapsIsReady;
  }
  set dataPropertyProfileMapsIsReady(dataPropertyProfileMapsIsReady) {
    this.updateTradeContractSource({dataPropertyProfileMapsIsReady}, false);
  }

  /**
   * HCM 送信用データのベースとなるオブジェクトを生成します
   *
   * @private
   */
  private buildData() {
    const issuerRole = (() => {
      if(this.isSelfConsume) {
        return this.amiProposer ? 'consumer' : 'producer';
      } else if(this.isSelfProduce) {
        return this.amiProposer ? 'producer' : 'consumer';
      }
      throw new Error(`不正な serviceType: ${this.serviceType}`);
    })();
    const issuerMapperRole = (() => {
      switch(this.contractType) {
        case 'recruitment':
        case 'draft':
          return 'proposer';
        case "offer":
          return 'responder';
        default:
          throw new Error(`不正な contractType: ${this.contractType}`);
      }
    })();

    return {
      issuerRole, // producer or consumer
      issuerMapperRole, // proposer or responder
      sendType: this.sendType,
      producerDataProfileId: this.tradeContractSource.producerDataProfileId,
      consumerDataProfileId: this.tradeContractSource.consumerDataProfileId,
      producerServiceProfileId: this.tradeContractSource.producerServiceProfileId,
      consumerServiceProfileId: this.tradeContractSource.consumerServiceProfileId,
      producerProcessProfileId: this.tradeContractSource.producerProcessProfileId,
      consumerProcessProfileId: this.tradeContractSource.consumerProcessProfileId,
      producerEventProfileIds: this.tradeContractSource.producerEventProfileIds,
      consumerEventProfileIds: this.tradeContractSource.consumerEventProfileIds,
      unchangeable: this.tradeContractSource.unchangeable,
      expireDate: this.tradeContractSource.expireDate,
      startDatetime: this.tradeContractSource.startDatetime,
      endDatetime: this.tradeContractSource.endDatetime,
      contractProfileTemplateId: this.contractProfileTemplateId, // 契約内容を示すプロファイルのテンプレートID
    };
  }

  /**
   * 取引契約作成用データを生成します
   */
  buildPostData() {
    const buildData = this.buildData();
    return {
      ...buildData,
      name: this.name, // 契約の名称
      description: this.description, // 契約内容の簡単な説明
      baseId: this.baseId,
      commonDictionaryId: this.commonDictionaryId, // 共通辞書ID
      contractType: this.contractType,
      recipientTerminalId: this.tradeContractSource.recipientTerminalId,
    };
  }

  /**
   * 取引契約更新用データを生成します
   *
   * @param containsMessage メッセージをデータに含める場合 true そうでない場合 false
   */
  buildPatchData(containsMessage: boolean) {
    const buildData = this.buildData();
    return {
      ...buildData,
      name: this.amiIssuer ? this.tradeContractSource.name : undefined,
      description: this.amiIssuer ? this.tradeContractSource.description : undefined,
      dataPropertyProfileMaps: this.tradeContractSource.dataPropertyProfileMaps,
      message: containsMessage ? this.tradeContractSource.message : undefined,
    };
  }

  /**
   * 指定されたリクエストタイプのメッセージが送信可能化どうかを返します
   *
   * @param requestType 契約メッセージのリクエストタイプ
   */
  transitable(requestType: keyof typeof CONTRACT_MESSAGE_REQUEST_TYPES) {
    switch(requestType) {
      case 'request':
        return (this.status === 'preparing' || this.status === 'revising') && this.isRequestMode;
      case 'publish':
        return this.status === 'preparing' && this.isPublishMode;
      case 'subscribe':
        return (this.status === 'preparing' || this.status === 'revising') && this.isSubscribeMode;
      case 'accept':
        return this.status === 'applying' || this.status === 'requesting';
      case 'revise':
        return this.status === 'applying' || this.status === 'requesting';
      case 'cancel':
        return this.status === 'revising';
      case 'reject':
        return this.status === 'requesting' || this.status === 'applying';
      case 'suspend':
        return this.status === 'public' || this.status === 'trading';
      case 'resume':
        return this.status === 'private' || this.status === 'suspended';
      case 'inquire':
        return true;
    }
  }

  /**
   * 取引契約の状態を遷移させます
   *
   * @param requestType 次状態に遷移させるリクエストタイプ
   */
  async transitTradeContract(requestType: keyof typeof CONTRACT_MESSAGE_REQUEST_TYPES | 'save') {
    // 遷移先ステータスを検証
    if(requestType !== 'save' && !this.transitable(requestType)) {
      throw new Error(`${requestType} のメッセージを送出することはできません`);
    }

    // 先頭の取引契約以外は遷移させない
    if(this.previousTradeContractId !== null) {
      requestType = 'save';
    }

    const patchTc = async (urlPostfix: string, data?: any) => {
      const {data: tradeContract} =
        await this.hcsClient.patch<TradeContract>(`/trade_contracts/${this.tradeContractSource.id}${urlPostfix}`, data);
      return tradeContract;
    };

    switch(requestType) {
      // 適応
      case 'accept':
        return await patchTc('/accept', this.buildPatchData(true));

      // 拒否
      case 'reject':
        return await patchTc('/reject', this.buildPatchData(true));

      // 募集
      case 'publish':
        await patchTc('', this.buildPatchData(false));
        return await patchTc('/publish');

      // 停止
      case 'suspend':
        return await patchTc('/suspend', this.buildPatchData(!this.isPublishMode));

      // 再開
      case 'resume':
        return await patchTc('/resume', this.buildPatchData(!this.isPublishMode));

      // その他
      default: {
        const result = await patchTc('', this.buildPatchData(false));

        // 契約メッセージの送信 (先頭の取引契約のみ)
        if (requestType !== 'save') {
          await this.hcsClient.post('/contract_messages', {
            requestType: requestType,
            tradeContractId: this.tradeContractSource.id,
            message: this.message
          });
        }

        return result;
      }
    }
  }

  /**
   * 取引契約を複製します
   *
   * @param expireDate 回答期限
   */
  async duplicate(expireDate?: DateTime) {
    if(!expireDate) {
      expireDate = DateTime.now().startOf('day').plus({days: 30});
    }

    const {data: newTc} = await this.hcsClient.post(`/trade_contracts/${this.tradeContractSource.id}/duplicate`, {
      expireDate: expireDate.toISO()
    });

    return new TradeContractView(newTc, this.$store);
  }

  async fetchContractProfiles(): Promise<ContractProfile[]> {
    try {
      const {data} = await this.hcsClient.get('/contract_profiles', {
        params: {
          tradeContractIdEq: this.tradeContract.id
        }
      });
      return data;
    } catch(e) {
      if(e instanceof UninitializedStoreError) {
        return [];
      }
      throw e;
    }
  }
}

/**
 * TradeContractView を生成します
 *
 * @param tradeContract TradeContract
 * @param store Vuex ストア
 */
export function createTradeContractView(tradeContract: TradeContract | TradeContractSource, store: Store<RootStateType>) {
  return new TradeContractView({
    message: '',
    dataPropertyProfileMapsIsReady: false,
    latestMessageCreatedAt: '',
    ...tradeContract,
  }, store);
}

/**
 * 空の TradeContractView を生成します
 */
export function emptyTradeContractView() {
  // @ts-ignore
  return new TradeContractView({}, {});
}
